import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaGamepad } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSiteLogo } from '../../utils/siteLogo';

const FooterSection = React.memo(() => {
    const { t } = useLanguage();
    const [customLogo, setCustomLogo] = useState(null);

    useEffect(() => {
        getSiteLogo().then(url => { if (url) setCustomLogo(url); });
    }, []);

    return (
        <footer className="py-4 px-4 sm:px-6 lg:px-8" style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-8">
                    {/* Brand */}
                    <div className="md:col-span-1">
                        <div className="mb-3">
                            {customLogo ? (
                                <img src={customLogo} alt="Logo" loading="lazy" decoding="async" className="h-[70px] object-contain" />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                        <FaGamepad className="text-white text-sm" />
                                    </div>
                                    <span className="font-bold text-gray-900 text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        Fastlane<span className="text-blue-500">Lv.</span>
                                    </span>
                                </div>
                            )}
                        </div>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            {t('footer.desc1')}<br />
                            {t('footer.desc2')}<br />
                            {t('footer.desc3')}
                        </p>
                    </div>

                    {/* Marketplace */}
                    <div>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{t('footer.marketplaceTitle')}</h4>
                        <ul className="space-y-2.5 text-sm text-gray-500">
                            <li><Link to="/marketplace" className="hover:text-blue-500 transition-colors">{t('footer.allGames')}</Link></li>
                            <li><Link to="/marketplace" className="hover:text-blue-500 transition-colors">{t('footer.gameAccounts')}</Link></li>
                            <li><Link to="/marketplace" className="hover:text-blue-500 transition-colors">{t('footer.itemsAndSkins')}</Link></li>
                            <li><Link to="/games" className="hover:text-blue-500 transition-colors">{t('footer.boostingService')}</Link></li>
                        </ul>
                    </div>

                    {/* Community */}
                    <div>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{t('footer.communityTitle')}</h4>
                        <ul className="space-y-3 text-sm text-gray-500">
                            <li>
                                <a href="https://www.facebook.com/NoSizeGamerTH" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors flex items-center gap-2.5">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                                    Facebook
                                </a>
                            </li>
                            <li>
                                <a href="https://www.youtube.com/@fastlanelv" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors flex items-center gap-2.5">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                                    YouTube
                                </a>
                            </li>
                            <li>
                                <a href="https://line.me/ti/g2/9NpHWCeoWKHXSxTocV5fYhqTX1EOrmQ_DGFTPA" target="_blank" rel="noopener noreferrer" className="hover:text-green-500 transition-colors flex items-center gap-2.5">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#06C755"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" /></svg>
                                    Line Group
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-6 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-gray-400 text-sm">{t('footer.allRightsReserved')}</p>
                    <p className="text-gray-400 text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        {t('footer.serverStatus')}
                    </p>
                </div>
            </div>
        </footer>
    );
});

FooterSection.displayName = 'FooterSection';

export default FooterSection;
